import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { Brand } from '@hygh-vybe/lib';
import { API_BASE, CATALOG_URL } from '../../config';

interface ClickReport {
  totalClicks: number;
  days: number;
  dailyTrend: Array<{ date: string; count: number }>;
  topBrands: Array<{ brand: string; count: number }>;
  topProducts: Array<{ productId: string; brandName: string; count: number }>;
  byReferral: Array<{ referredBy: string; count: number }>;
}

interface ReferralReport {
  totalUsers: number;
  usersWithReferral: number;
  breakdown: Array<{ source: string; count: number }>;
}

const SOURCE_LABELS: Record<string, string> = {
  friend: 'A friend',
  social_media: 'Social media',
  google: 'Google search',
  podcast: 'Podcast',
  event: 'Event / trade show',
  other: 'Other',
};

export function AdminOverview() {
  const { authFetch } = useAuth();
  const [totalProducts, setTotalProducts] = useState<number | null>(null);
  const [affiliateBrands, setAffiliateBrands] = useState<number | null>(null);
  const [lastScraperRun] = useState<string | null>(null);
  const [clickReport, setClickReport] = useState<ClickReport | null>(null);
  const [referralReport, setReferralReport] = useState<ReferralReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  // Fetch stats (products from catalog JSON, brands from API)
  useEffect(() => {
    async function fetchStats() {
      try {
        // Total products from static catalog JSON (fast)
        const catalogRes = await fetch(CATALOG_URL);
        if (catalogRes.ok) {
          const json = await catalogRes.json();
          const products = json.products || json;
          setTotalProducts(Array.isArray(products) ? products.length : 0);
        }

        // Affiliate brands: count brands with affiliateNetwork set
        const brandsRes = await authFetch(`${API_BASE}/v1/brands`);
        if (brandsRes.ok) {
          const json = await brandsRes.json();
          const brands = (json.data || []) as Brand[];
          const withAffiliate = brands.filter(
            (b) => b.affiliateNetwork && b.affiliateNetwork.trim() !== '',
          );
          setAffiliateBrands(withAffiliate.length);
        }
      } catch {
        // Stats will show as unavailable
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, [authFetch]);

  // Fetch reports
  useEffect(() => {
    async function fetchReports() {
      try {
        const [clicksRes, referralsRes] = await Promise.all([
          authFetch(`${API_BASE}/v1/admin/reports?type=clicks&days=${days}`),
          authFetch(`${API_BASE}/v1/admin/reports?type=referrals`),
        ]);

        if (clicksRes.ok) {
          const json = await clicksRes.json();
          setClickReport(json.data);
        }
        if (referralsRes.ok) {
          const json = await referralsRes.json();
          setReferralReport(json.data);
        }
      } catch {
        // Non-critical
      }
    }

    fetchReports();
  }, [authFetch, days]);

  if (loading) {
    return <div className="admin-loading">Loading stats...</div>;
  }

  return (
    <div>
      <h1>Dashboard Overview</h1>
      <div className="admin-stats">
        <div className="admin-stat-card">
          <div className="stat-label">Total Products</div>
          <div className="stat-value">
            {totalProducts != null ? totalProducts : '—'}
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-label">Affiliate Brands</div>
          <div className="stat-value">
            {affiliateBrands != null ? (
              <Link to="/admin/brands?filter=affiliates" style={{ color: 'inherit', textDecoration: 'underline' }}>
                {affiliateBrands}
              </Link>
            ) : '—'}
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-label">Last Scraper Run</div>
          <div className="stat-value" style={{ fontSize: '1rem' }}>
            {lastScraperRun || 'No runs yet'}
          </div>
          <div className="stat-sub">Automated product data collection</div>
        </div>
      </div>

      {/* Buy Click Analytics */}
      <div className="admin-scraper-section" style={{ marginTop: '32px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0 }}>Buy Now Clicks</h3>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--color-border)' }}
          >
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>

        {clickReport ? (
          <>
            <div className="admin-stats" style={{ marginBottom: '16px' }}>
              <div className="admin-stat-card">
                <div className="stat-label">Total Clicks</div>
                <div className="stat-value">{clickReport.totalClicks}</div>
                <div className="stat-sub">Last {clickReport.days} days</div>
              </div>
              <div className="admin-stat-card">
                <div className="stat-label">Avg / Day</div>
                <div className="stat-value">{clickReport.days > 0 ? (clickReport.totalClicks / clickReport.days).toFixed(1) : 0}</div>
              </div>
            </div>

            {/* Daily Trend */}
            {clickReport.dailyTrend.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '8px' }}>Daily Trend</h4>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '60px' }}>
                  {clickReport.dailyTrend.map((day) => {
                    const maxCount = Math.max(...clickReport.dailyTrend.map(d => d.count), 1);
                    const height = (day.count / maxCount) * 100;
                    return (
                      <div
                        key={day.date}
                        title={`${day.date}: ${day.count} clicks`}
                        style={{
                          flex: 1,
                          height: `${Math.max(height, 4)}%`,
                          backgroundColor: 'var(--color-primary)',
                          borderRadius: '2px 2px 0 0',
                          minWidth: '3px',
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Top Brands */}
            {clickReport.topBrands.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '8px' }}>Top Brands</h4>
                <div className="admin-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr><th>Brand</th><th>Clicks</th></tr>
                    </thead>
                    <tbody>
                      {clickReport.topBrands.map((b) => (
                        <tr key={b.brand}><td>{b.brand}</td><td>{b.count}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Clicks by Referral */}
            {clickReport.byReferral.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '8px' }}>Clicks by Referral</h4>
                <div className="admin-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr><th>Referred By</th><th>Clicks</th></tr>
                    </thead>
                    <tbody>
                      {clickReport.byReferral.map((r) => (
                        <tr key={r.referredBy}><td>{r.referredBy}</td><td>{r.count}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>No click data yet.</p>
        )}
      </div>

      {/* Referral Analytics */}
      <div className="admin-scraper-section">
        <h3>Referral Sources</h3>

        {referralReport ? (
          <>
            <div className="admin-stats" style={{ marginBottom: '16px' }}>
              <div className="admin-stat-card">
                <div className="stat-label">Total Users</div>
                <div className="stat-value">{referralReport.totalUsers}</div>
              </div>
              <div className="admin-stat-card">
                <div className="stat-label">With Referral</div>
                <div className="stat-value">{referralReport.usersWithReferral}</div>
                <div className="stat-sub">
                  {referralReport.totalUsers > 0
                    ? `${((referralReport.usersWithReferral / referralReport.totalUsers) * 100).toFixed(0)}%`
                    : '0%'}
                </div>
              </div>
            </div>

            {referralReport.breakdown.length > 0 && (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr><th>Source</th><th>Users</th></tr>
                  </thead>
                  <tbody>
                    {referralReport.breakdown.map((r) => (
                      <tr key={r.source}>
                        <td>{SOURCE_LABELS[r.source] || r.source}</td>
                        <td>{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>No referral data yet.</p>
        )}
      </div>
    </div>
  );
}
